// @ts-strict-ignore
import {
  ErrorBoundary,
  Text,
} from '@usealloy/component-library';
import { useState } from 'react';
import type { Document } from '~/entities/types';
import { DocReviewActions } from './components/DocReviewActions';
import { DocReviewBody } from './components/DocReviewBody';
import { DocReviewHeader } from './components/DocReviewHeader';
import { DocReviewNavigator } from './components/DocReviewNavigator';
import { DocReviewNotes } from './components/DocReviewNotes';

interface Props {
  documents: Document[];
  hideReviewActions?: boolean;
  onRenameSuccess?: (args: { document_token: string; entity_token: string }) => void;
  onReviewSuccess?: (args: { document_token: string; entity_token: string }) => void;
  shouldPinReviewActions?: boolean;
}

export const DocumentReviewer = ({
  documents,
  hideReviewActions = false,
  onRenameSuccess,
  onReviewSuccess,
  shouldPinReviewActions = false,
}: Props) => {
  const [activeDocToken, setActiveDocToken] = useState(documents.at(0)?.document_token);

  const activeDocument = documents.find(data => data.document_token === activeDocToken);

  return (
    <ErrorBoundary>
      <DocReviewNavigator
        documents={documents}
        activeDocToken={activeDocToken}
        setActiveDocToken={setActiveDocToken}
      />
      { activeDocument ? (
        <>
          <DocReviewHeader
            key={`header-${activeDocument?.document_id}`}
            document={activeDocument}
            onRenameDocSuccess={() => {
              onRenameSuccess?.({
                document_token: activeDocToken,
                entity_token: activeDocument.entity_token,
              });
            }}
          />
          <DocReviewBody
            key={`body-${activeDocument?.document_id}`}
            document={activeDocument}
          />
          <DocReviewNotes
            document={activeDocument}
            onNoteAddSuccess={() => {
              onReviewSuccess?.({
                document_token: activeDocToken,
                entity_token: activeDocument.entity_token,
              });
            }}
          />
          <DocReviewActions
            key={`actions-${activeDocument?.document_id}`}
            document={activeDocument}
            hideReviewActions={hideReviewActions}
            onReviewSuccess={() => {
              onReviewSuccess?.({
                document_token: activeDocToken,
                entity_token: activeDocument.entity_token,
              });
            }}
            shouldPinReviewActions={shouldPinReviewActions}
          />
        </>
      ) : (
        <Text>
          Unable to load the selected document. Please try selecting another document.
        </Text>
      )}
    </ErrorBoundary>
  );
};
